import { FC } from 'react';

import { Transaction, TransactionStatus } from '@ravago/shared/page-data/models/elements';
import { Badge } from '@ravago/shared/radiance/components/badge/components/Badge/Badge';
import { Box } from '@ravago/shared/radiance/components/box/components/Box/Box';
import { IconButton } from '@ravago/shared/radiance/components/button/components/IconButton/IconButton';
import { Checkbox } from '@ravago/shared/radiance/components/checkbox/components/Checkbox/Checkbox';
import { Spinner } from '@ravago/shared/radiance/components/spinner/components/Spinner/Spinner';
import { TableCell } from '@ravago/shared/radiance/components/table/components/TableCell/TableCell';
import { TableRow } from '@ravago/shared/radiance/components/table/components/TableRow/TableRow';
import { Tooltip } from '@ravago/shared/radiance/components/tooltip/components/Tooltip/Tooltip';
import { Typography } from '@ravago/shared/radiance/components/typography/components/Typography/Typography';
import { useFormatter, useTranslations } from '@ravago/shared/translations/client';

import { useInvoiceDocument } from '../../../../../hooks/use-invoice-document.hook';
import { canPayInvoiceOnline, canSelectInvoice } from '../../../../../utils/invoice.utils';
import { InvoiceDueDate } from '../shared/invoice-due-date';

type Props = {
   invoice: Transaction;
   canPayOnline: boolean;
   selected: boolean;
   onSelect: () => void;
   onDeselect: () => void;
};

const SHOW_TOOLTIP_LENGTH = 18;

export const OpenInvoiceTableRow: FC<Props> = ({ invoice, canPayOnline, selected, onSelect, onDeselect }) => {
   const { isDownloading, handleDownload, handleView } = useInvoiceDocument();
   const translate = useTranslations();
   const format = useFormatter();

   const isPartiallyPaid = invoice.amount !== invoice.openAmount && invoice.openAmount > 0;

   const invoicePayload = invoice.documentId
      ? { invoiceId: invoice.id, documentId: invoice.documentId, date: invoice.issueDate, type: invoice.type, eInvoiceId: invoice.eInvoiceId }
      : null;

   const showTooltipOnInvoiceId = invoice.eInvoiceId?.length || SHOW_TOOLTIP_LENGTH < 0;

   return (
      <TableRow>
         {canPayOnline && (
            <TableCell>
               {canPayInvoiceOnline(invoice) ? (
                  <Checkbox
                     checked={selected}
                     onChange={(isSelected) => (isSelected ? onSelect() : onDeselect())}
                     disabled={!canSelectInvoice(invoice)}
                  />
               ) : (
                  <Tooltip pointers={false} direction="right" text={translate('invoices.open-invoices.table.invalid-payment-media')}>
                     <Checkbox disabled />
                  </Tooltip>
               )}
            </TableCell>
         )}
         <TableCell width="140px">
            <Box direction="column" gap="3xs">
               {showTooltipOnInvoiceId && (
                  <Tooltip text={invoice.eInvoiceId ?? invoice.documentId?.toString() ?? invoice.id} limitWidth={false}>
                     <span
                        style={{
                           display: 'block',
                           textOverflow: 'ellipsis',
                           overflow: 'hidden'
                        }}
                     >
                        <Typography variant="small">{invoice.eInvoiceId ?? invoice.documentId ?? invoice.id}</Typography>
                     </span>
                  </Tooltip>
               )}
               {!showTooltipOnInvoiceId && (
                  <span
                     style={{
                        display: 'block',
                        textOverflow: 'ellipsis',
                        overflow: 'hidden'
                     }}
                  >
                     <Typography variant="small">{invoice.eInvoiceId || invoice.documentId}</Typography>
                  </span>
               )}
               <Typography component="span" variant="small" color="subtle">
                  {translate(`invoices.invoice-type.${invoice.type}`)}
               </Typography>
            </Box>
         </TableCell>
         <TableCell>
            <Typography component="span" variant="small">
               {format.dateTime(invoice.issueDate)}
            </Typography>
         </TableCell>
         <TableCell>{invoice.dueDate && <InvoiceDueDate dueDate={invoice.dueDate} invoiceType={invoice.type} />}</TableCell>
         <TableCell>
            <Box direction="column" gap="3xs" items="end">
               <Typography variant="small">{format.number(invoice.openAmount, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}</Typography>
               {isPartiallyPaid && (
                  <Typography variant="caption">
                     {translate('invoices.open-invoices.table.invoice-amount', {
                        amount: format.number(invoice.amount, {
                           minimumFractionDigits: 2,
                           maximumFractionDigits: 2
                        })
                     })}
                  </Typography>
               )}
            </Box>
         </TableCell>
         <TableCell align="right">
            <Box direction="column" gap="2xs" justify="end">
               {isPartiallyPaid && <Badge variant="warning">{translate('invoices.warning.partially-paid')}</Badge>}
               {invoice.status === TransactionStatus.PENDING && (
                  <Badge leadingIcon="lock" variant="neutral">
                     {translate('invoices.warning.pending')}
                  </Badge>
               )}
               {invoice.status === TransactionStatus.PAID && (
                  <Tooltip text={translate('invoices.warning.paid-and-processing-tooltip')}>
                     <Badge trailingIcon="info-circle" variant="success">
                        {translate('invoices.warning.paid-and-processing')}
                     </Badge>
                  </Tooltip>
               )}
            </Box>
         </TableCell>
         <TableCell align="right">
            {!!invoicePayload && (
               <Box justify="end">
                  {isDownloading ? (
                     <Spinner size="small" />
                  ) : (
                     <Box>
                        <IconButton
                           icon="download"
                           ariaLabel="Download Invoice"
                           onClick={() => handleDownload(invoicePayload)}
                           showGutters={false}
                           variant="on-light"
                        />
                        <IconButton
                           icon="eye"
                           ariaLabel="View Invoice"
                           onClick={() => handleView(invoicePayload)}
                           showGutters={false}
                           variant="on-light"
                        />
                     </Box>
                  )}
               </Box>
            )}
         </TableCell>
      </TableRow>
   );
};
